import { Box, Button, HStack, SimpleGrid, Text, useToast } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ChevronLeftIcon, PenIcon } from 'lucide-react';
import { useSnapshot } from 'valtio';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { SingleField } from '@duke-hq/ui/components/SingleField/SingleField';
import { djs } from '@duke-hq/djs';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { useState, useMemo } from 'react';
import { internalEnrolmentState } from '@duke-hq/app-provider-web/tenant/tenantState/InternalEnrolmentState';
import { useAuthTenant } from '@duke-hq/app-provider-web/tenant/useAuthTenant';
import { getCourseDatesFromAdminCourseSchedule, toUtcStartOfDay } from '@duke-hq/lib/dates';
import { CourseSegment, WorkSchedule } from '@duke-hq/shared-constants/enrolments';

type SummaryProps = {
  goToPrevStep: () => void;
  setStep: (step: React.SetStateAction<number>) => void;
};

enum EnrolmentPace {
  FULL_TIME = 'Full time',
  PART_TIME = 'Part time',
}

function SummarySectionHeading({
  children,
  onEditClick,
}: {
  children: React.ReactNode;
  onEditClick: () => void;
}) {
  return (
    <HStack justifyContent="space-between" w="full">
      <Text fontSize="md" fontWeight="semibold" lineHeight="6" color="fg.emphasis">
        {children}
      </Text>
      <Button variant="ghost" onClick={onEditClick} leftIcon={<PenIcon size="20px" />} size="sm">
        <Trans>Edit</Trans>
      </Button>
    </HStack>
  );
}

export function SkillSetSummary({ goToPrevStep, setStep }: SummaryProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const utils = trpcTenant.useUtils();
  const snap = useSnapshot(internalEnrolmentState);
  const toast = useToast();
  const auth = useAuthTenant();
  const createStudent = trpcTenant.users.createStudent.useMutation();
  const createCourseEnrolment = trpcTenant.enrolments.createCourseEnrolment.useMutation({
    onSuccess: () => {
      toast({
        title: t`Course enrolment created successfully.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) => {
      setIsSubmitting(false);
      toast({
        title: t`An error occurred.`,
        description: error.message,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
    onSettled: async () => {
      setIsSubmitting(false);
      snap.closeModal();
      snap.resetStates();

      await Promise.all([
        utils.enrolments.getMemberInternalEnrolmentCount.refetch(),
        utils.enrolments.all.refetch(),
        utils.enrolments.getByUserEmail.invalidate({ email: snap.selectedUserAttributes.email }),
      ]);
    },
  });
  const { data: course, isLoading } = trpcTenant.courses.courseSchedule.useQuery(
    snap.selectedCourseAttributes.id,
  );

  const courseStartAndEndDates = useMemo(() => {
    if (course?.schedule == null || isLoading) {
      return null;
    }

    try {
      return getCourseDatesFromAdminCourseSchedule({
        adminCourseScheduleItems: course.schedule,
        courseSegment: snap.enrolmentSettingsData.course_segment ?? CourseSegment.FULL_COURSE,
        workSchedule: snap.enrolmentSettingsData.work_schedule ?? WorkSchedule.FULL_TIME,
        startAt: toUtcStartOfDay(snap.enrolmentSettingsData.start_at_date, 'YYYY-MM-DD'),
      });
    } catch {
      return null;
    }
  }, [
    course?.schedule,
    isLoading,
    snap.enrolmentSettingsData.course_segment,
    snap.enrolmentSettingsData.start_at_date,
    snap.enrolmentSettingsData.work_schedule,
  ]);

  const handleSubmit = async () => {
    const {
      selectedUserAttributes,
      selectedParticipant,
      selectedCourseAttributes,
      enrolmentSettingsData,
    } = snap;

    const buildCourseEnrolmentPayload = (userId: string) => ({
      user_id: userId,
      course_id: selectedCourseAttributes.id,
      start_at_date: enrolmentSettingsData.start_at_date,
      enrolment_contract_type: enrolmentSettingsData.enrolment_contract_type,
      contract_category: enrolmentSettingsData.contract_category,
      work_schedule: enrolmentSettingsData.work_schedule,
      course_segment: enrolmentSettingsData.course_segment,
      user_timezone: djs.tz.guess(),
      consultant_id: auth.user.id,
      site_location_id: enrolmentSettingsData.site_location.site_id,
      external_id: enrolmentSettingsData?.external_id,
      is_internal_enrolment: true,
    });

    setIsSubmitting(true);

    if (typeof selectedParticipant.id === 'string' && selectedParticipant.id.trim() !== '') {
      const payload = buildCourseEnrolmentPayload(selectedParticipant.id);
      await createCourseEnrolment.mutateAsync(payload);
      return;
    }

    // Otherwise, create the user then enrol them
    const createStudentPayload = {
      email: selectedUserAttributes.email,
      first_name: selectedUserAttributes.first_name,
      last_name: selectedUserAttributes.last_name,
      studentData: {
        owned_by: auth.user.id,
      },
    };

    const user = await createStudent.mutateAsync(createStudentPayload);

    if (user?.id) {
      const payload = buildCourseEnrolmentPayload(user.id);
      await createCourseEnrolment.mutateAsync(payload);
    }
  };

  return (
    <FormMainContainer>
      <FormContentContainer>
        <Box w="full">
          <SummarySectionHeading onEditClick={() => setStep(1)}>
            <Trans>Course</Trans>
          </SummarySectionHeading>
          <SimpleGrid columns={1} w="full">
            <SingleField
              name={snap.selectedCourseAttributes.identifier}
              value={snap.selectedCourseAttributes.title}
            />
            <SingleField
              name={t`Cost`}
              value={`$${snap.selectedCourseAttributes.price_aud.toString()}`}
            />
          </SimpleGrid>
        </Box>
        <Box w="full">
          <SummarySectionHeading onEditClick={() => setStep(3)}>
            <Trans>Enrolment settings</Trans>
          </SummarySectionHeading>
          <SimpleGrid columns={2} w="full">
            <SingleField
              name={<Trans>Manager</Trans>}
              value={`${auth.user.first_name} ${auth.user.last_name} (myself)`}
            />

            <SingleField
              name={<Trans>Start date</Trans>}
              value={djs(snap.enrolmentSettingsData.start_at_date).format('dddd, DD MMMM YYYY')}
            />
            <SingleField
              name={<Trans>Purchase order</Trans>}
              value={snap.enrolmentSettingsData.external_id}
            />
            <SingleField
              name={<Trans>Enrolment pace</Trans>}
              value={EnrolmentPace[snap.enrolmentSettingsData.work_schedule]}
            />
          </SimpleGrid>
          {courseStartAndEndDates?.courseEndDate ? (
            <Text
              color="fg.emphasis"
              textStyle="sm"
              textAlign="left"
              fontWeight="normal"
              w="full"
              mt={2}
            >
              <Trans>Course is expected to end by</Trans>{' '}
              {courseStartAndEndDates.courseEndDate.long}
            </Text>
          ) : null}
        </Box>
      </FormContentContainer>
      <FormFooterContainer>
        <Button onClick={handleSubmit} isLoading={isSubmitting}>
          <Trans>Create and refer participant</Trans>
        </Button>
        <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
          <Trans>Back</Trans>
        </Button>
      </FormFooterContainer>
    </FormMainContainer>
  );
}
